import React, { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Search used on the public website forms. */
export const publicCompanySearch = (q) =>
  fetch(`${BACKEND_URL}/api/trials/companies?q=${encodeURIComponent(q)}`).then((r) => (r.ok ? r.json() : { items: [] }));

/**
 * Company name with Companies House suggestions (UK companies). Picking one sets the company number, so we can
 * fill in the registered details; typing a name by hand still works (sole traders, non-UK companies).
 * `search` must be a stable function returning {items: [...]}.
 */
const CompanyField = ({ label = 'Company', value, number, onChange, search, inputClassName, placeholder }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [searched, setSearched] = useState('');

  useEffect(() => {
    const q = (value || '').trim();
    if (number || q.length < 3) { setItems([]); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      search(q).then((d) => {
        if (!live) return;
        setItems(d?.items || []);
        setUnavailable(d?.available === false);
        setSearched(q);
      }).catch(() => { if (live) { setItems([]); setUnavailable(true); setSearched(q); } });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [value, number, search]);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700">{label}
        <input value={value} autoComplete="organization" placeholder={placeholder}
          onChange={(e) => { onChange(e.target.value, null); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          className={inputClassName || 'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0a1f2e] focus:outline-none'} />
      </label>
      {number && (
        <p className="mt-1 text-xs text-green-700">Companies House company {number}: we will fill in your registered details.{' '}
          <button type="button" className="underline text-gray-500" onClick={() => onChange(value, null)}>Not this company</button></p>
      )}
      {!number && unavailable && searched && (
        <p className="mt-1 text-xs text-amber-700">Company search isn't available right now. Type your 8-character Companies House
          number (for example 12345678 or SC123456) to continue, or add your details by hand.</p>
      )}
      {!number && !unavailable && searched && items.length === 0 && (value || '').trim() === searched && (
        <p className="mt-1 text-xs text-gray-500">No company found with that name. Try your company number instead.</p>
      )}
      {open && !number && items.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white text-left shadow-lg">
          {items.map((c) => (
            <li key={c.company_number}>
              <button type="button" className="block w-full px-3 py-2 text-left hover:bg-gray-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(c.name, c.company_number); setOpen(false); }}>
                <div className="text-sm font-medium text-[#0a1f2e]">{c.name}
                  {c.status && c.status !== 'active' && <span className="ml-2 text-xs text-red-600">({c.status})</span>}</div>
                <div className="text-xs text-gray-500">{c.company_number} · {c.address}</div>
              </button>
            </li>
          ))}
          <li className="px-3 py-1.5 text-[11px] text-gray-400">From Companies House. Not listed? Just type your company name.</li>
        </ul>
      )}
    </div>
  );
};

export default CompanyField;
